import React from 'react';
import styled from 'styled-components';
import { theUserIsInGermany } from '@peloton/internationalize/consolidatedAppConfigs/utils';
import scrollToTop from '@peloton/navigation/scrollToTop';
import { defaultTransition } from '@peloton/styles';
import { gray1, slate1, white } from '@engage/colors';
import type { SelectableToggleFilterGroup, toggle } from '@engage/filters';
import { label14, label6Large } from '@engage/typography';
import { useLocale } from '@members/localization';
import NotTaken from '../icons/NotTaken';
import NoWeights from '../icons/NoWeights';
import Taken from '../icons/Taken';
import Weights from '../icons/Weights';

const SELECTED_CLASSNAME = 'toggle-on';

const TakenButton = styled.button`
  background: ${gray1};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  color: ${slate1};
  flex: 1;
  height: 56px;
  text-align: center;
  width: 50%;
  ${label6Large}
  font-weight: 400;
  margin-left: 5px;
  margin-right: 5px;
  ${defaultTransition('filter')}
  &:hover {
    filter: brightness(95%);
  }

  &.${SELECTED_CLASSNAME} {
    background: ${slate1};
    color: ${white};
  }

  &:first-child {
    margin-left: 0px;
  }
  &:last-child {
    margin-right: 0px;
  }
`;

const StyledIcon = styled.div<{ isSelected: boolean; filteringType: string }>`
  margin-right: 14px;

  svg {
    path {
      stroke: ${p =>
        // eslint-disable-next-line no-nested-ternary
        p.isSelected && p.filteringType === 'has_workout'
          ? `${white}`
          : !p.isSelected && p.filteringType === 'has_workout'
          ? `${slate1}`
          : ''};
      fill: ${p =>
        // eslint-disable-next-line no-nested-ternary
        p.isSelected && p.filteringType === 'has_cycling_arms'
          ? `${white}`
          : !p.isSelected && p.filteringType === 'has_cycling_arms'
          ? `${slate1}`
          : ''};
    }
  }
`;

const TakenText = styled.header`
  ${label14}
  overflow-wrap: break-word;
`;

const TakenTextLeft = styled.header`
  ${label14}
  overflow-wrap: break-word;
  inline-size: 110px;
  text-align: left;
`;

const isSelected = (group: SelectableToggleFilterGroup, order: number): boolean =>
  Boolean(group.values.length && group.values[order].selected);

export const TakenToggleFilter: React.FC<React.PropsWithChildren<Props>> = ({
  filterGroup,
  toggleFilter,
  order,
}) => {
  const locale = useLocale();
  const displayName = filterGroup.values[order ?? 0].displayName;

  const filterGroupIconMap = {
    ['has_workout']: {
      toggle: <Taken />,
      unToggle: <NotTaken />,
    },
    ['has_cycling_arms']: {
      toggle: <Weights />,
      unToggle: <NoWeights />,
    },
  };

  return (
    <TakenButton
      onClick={() => {
        toggleFilter(
          filterGroup.name,
          order ? filterGroup.values[order].value : 'true',
          filterGroup.displayName,
          true,
        );
        scrollToTop();
      }}
      className={isSelected(filterGroup, order ?? 0) ? SELECTED_CLASSNAME : ''}
      data-test-id="toggleFilterButton"
      aria-pressed={isSelected(filterGroup, order ?? 0)}
    >
      <StyledIcon
        isSelected={isSelected(filterGroup, order ?? 0)}
        filteringType={filterGroup.name}
      >
        {order == 0
          ? filterGroupIconMap[filterGroup.name].toggle
          : filterGroupIconMap[filterGroup.name].unToggle}
      </StyledIcon>
      {theUserIsInGermany(locale) ? (
        <TakenTextLeft>{displayName}</TakenTextLeft>
      ) : (
        <TakenText>{displayName}</TakenText>
      )}
    </TakenButton>
  );
};

type Props = {
  filterGroup: SelectableToggleFilterGroup;
  toggleFilter: typeof toggle;
  order?: number;
};
